import React, { ReactNode, ReactElement } from 'react';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { FontAwesome } from '../FontAwesome';

import './style.scss';
import { InputLabel } from '../InputLabel/component';
import { AutoSaveState } from '../AutoSaveIndicator/component';

declare interface TextFieldButtonProps {
  label: string;
  hideLabel?: boolean;
  icon?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const TextFieldButton = React.memo(
  ({
    icon,
    label,
    hideLabel = false,
    onClick,
  }: TextFieldButtonProps): ReactElement => (
    <button type="button" onClick={onClick} className="ol-text-field-button">
      {icon && (
        <FontAwesome name={icon} className="ol-text-field-button-icon" />
      )}
      <span
        className={classNames('ol-text-field-button-label', {
          hidden: hideLabel,
        })}
      >
        {label}
      </span>
    </button>
  )
);

export declare interface TextFieldProps {
  id?: string;
  name?: string;
  label?: ReactNode;
  className?: string;
  value?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  maxLength?: number;
  type?: 'text' | 'url' | 'password' | 'number';
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  icon?: string;
  description?: ReactNode;
  error?: string | null;
  required?: boolean;
  focusOnMount?: boolean;
  button?: TextFieldButtonProps;
  autoSave?: boolean;
  autoSaveState?: AutoSaveState;
  autoComplete?: string;
  inline?: boolean;
  disabled?: boolean;
}

export class TextField extends React.PureComponent<TextFieldProps> {
  private inputId: string;

  private inputRef: React.RefObject<HTMLInputElement>;

  public constructor(props: TextFieldProps) {
    super(props);
    this.inputId = uniqueId();
    this.inputRef = React.createRef();
  }

  public componentDidMount(): void {
    const { focusOnMount } = this.props;

    if (focusOnMount && this.inputRef && this.inputRef.current) {
      this.inputRef.current.focus();
    }
  }

  private onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { onChange, onValueChange } = this.props;

    if (onChange) {
      onChange(event);
    }

    if (onValueChange) {
      onValueChange(event.target.value);
    }
  };

  private onFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus(event);
    }
  };

  private onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { onBlur } = this.props;
    if (onBlur) {
      onBlur(event);
    }
  };

  public render(): ReactNode {
    const {
      className,
      icon,
      label,
      description,
      error,
      value,
      required = false,
      button,
      maxLength,
      type = 'text',
      id = this.inputId,
      name,
      placeholder,
      onClick,
      onKeyUp,
      onKeyDown,
      autoSave = false,
      autoSaveState,
      autoComplete,

      // TODO: change the default to "false", need to go through all the
      // existing instances and add "inline" attribute to them.
      // Should do the same to other input components to be consistent.
      inline = true,
      disabled = false,
    } = this.props;

    return (
      <div
        className={classNames(
          'ol-text-field',
          { 'ol-text-field-inline': inline },
          className
        )}
      >
        {label && (
          <InputLabel
            htmlFor={this.inputId}
            error={error}
            required={required}
            autoSave={autoSave}
            autoSaveState={autoSaveState}
          >
            {label}
          </InputLabel>
        )}

        {description && (
          <span className="ol-text-field-description">{description}</span>
        )}
        <span className="ol-text-field-input-wrapper">
          {icon && (
            <div className="ol-text-field-icon-wrapper">
              <FontAwesome name={icon} className="ol-text-field-icon" />
            </div>
          )}
          <input
            {...{
              id,
              name,
              placeholder,
              type,
              maxLength,
              required,
              onClick,
              onKeyUp,
              onKeyDown,
              autoComplete,
              disabled,
            }}
            className="ol-text-field-text"
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            value={value || ''}
            ref={this.inputRef}
          />
          {button && <TextFieldButton {...button} />}
        </span>
      </div>
    );
  }
}
